import React from 'react';
import { Link } from 'react-router-dom';
import { formatCount, formatKr, formatClock } from '../lib/format';
import Sparkline from './Sparkline';
import CancellationsCard from './CancellationsCard';

/**
 * DOM overlay of parchment-style stat cards around the village scene.
 * Aggregate counts from GET /api/admin/metrics — never user ids. The one
 * exception to "counts only" is the cancellation free text, which stays
 * collapsed until opened (CancellationsCard). `ga` in the response is
 * ignored (always null for now).
 */

const clamp01 = (n) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

function Stops({ count }) {
  const n = Number(count) || 0;
  return (
    <div className="admin-card__sub admin-card__sub--muted">
      {formatCount(n)} stoppet
    </div>
  );
}

export default function StatPanels({ data, lastUpdated, stale }) {
  const now = data?.now || {};
  const week = data?.week || {};
  const month = data?.month || {};
  const milestones = now.milestones || {};
  const activeCount = Number(now.activeCount) || 0;
  const next = Number(milestones.next) || null;
  const progress = clamp01(
    Number.isFinite(Number(milestones.progress))
      ? Number(milestones.progress)
      : (next ? activeCount / next : 0)
  );
  const reached = next !== null && activeCount >= next;

  return (
    <div className="admin-village__overlay">
      <div className="admin-village__row">
        <section className="admin-card" aria-label="Innbyggere">
          <div className="admin-card__label">Innbyggere</div>
          <div className="admin-card__value admin-card__value--hero">{formatCount(activeCount)}</div>
          <div className="admin-card__sub">aktive abonnement</div>
        </section>

        <header className="admin-card admin-card--title">
          <div className="admin-card__title">Coord-landsbyen</div>
          <div className={`admin-card__clock${stale ? ' admin-card__clock--stale' : ''}`}>
            {stale ? 'Kunne ikke oppdatere · ' : ''}
            sist oppdatert {formatClock(lastUpdated)}
          </div>
          <Link to="/home" className="admin-card__back">← Tilbake til Coord</Link>
        </header>

        <section className="admin-card" aria-label="MRR">
          <div className="admin-card__label">MRR</div>
          <div className="admin-card__value admin-card__value--accent">{formatKr(now.mrrOre)}</div>
          <div className="admin-card__sub">per måned</div>
        </section>
      </div>

      <div className="admin-village__row admin-village__row--bottom">
        <section className="admin-card admin-card--pair" aria-label="Nye abonnenter">
          <div>
            <div className="admin-card__label">Nye denne uken</div>
            <div className="admin-card__value admin-card__value--small">{formatCount(week.newActivations)}</div>
            <Stops count={week.stops} />
          </div>
          <div>
            <div className="admin-card__label">Nye denne måneden</div>
            <div className="admin-card__value admin-card__value--small">{formatCount(month.newActivations)}</div>
            <Stops count={month.stops} />
          </div>
        </section>

        <CancellationsCard cancellations={data?.cancellations} />

        <section className="admin-card admin-card--wide" aria-label="Mot neste milepæl">
          <div className="admin-card__label">Mot neste milepæl</div>
          <div className="admin-card__milestone">
            <span className="admin-card__value admin-card__value--small">{formatCount(activeCount)}</span>
            <span className="admin-card__sub">
              {next ? `av ${formatCount(next)}${reached ? ' · nådd!' : ''}` : ''}
            </span>
          </div>
          <div className="admin-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(progress * 100)}>
            <div className="admin-progress__bar" style={{ width: `${(progress * 100).toFixed(1)}%` }} />
          </div>
          <div className="admin-card__label admin-card__label--spaced">Trend</div>
          <Sparkline series={data?.series} field="activeCount" label="Innbyggere over tid" />
        </section>
      </div>
    </div>
  );
}
